type ErrorWithDetails = {
  message?: string;
  networkError?: {
    message?: string;
    statusCode?: number;
  } | null;
  graphQLErrors?: Array<{
    message?: string;
    extensions?: {
      code?: string;
    };
  }>;
};

const PAYMENT_FALLBACK =
  "We couldn't start your payment. Please try again. If the problem continues, use another payment option.";

/**
 * Converts technical GraphQL, network, and payment-provider errors into short,
 * actionable copy that is safe to show to a guest.
 */
export const getPaymentErrorMessage = (error: unknown): string => {
  const details =
    error && typeof error === "object" ? (error as ErrorWithDetails) : null;
  const messages = [
    typeof error === "string" ? error : "",
    details?.message,
    details?.networkError?.message,
    ...(details?.graphQLErrors?.map((item) => item.message) ?? []),
    ...(details?.graphQLErrors?.map((item) => item.extensions?.code) ?? []),
  ]
    .filter((message): message is string => Boolean(message))
    .join(" ")
    .toLowerCase();

  const statusCode = details?.networkError?.statusCode;

  if (
    (typeof navigator !== "undefined" && !navigator.onLine) ||
    statusCode === 0 ||
    /failed to fetch|network request failed|network error|econn|timeout/.test(
      messages
    )
  ) {
    return "We couldn't connect. Check your internet connection and try again.";
  }

  if (
    statusCode === 401 ||
    statusCode === 403 ||
    /unauthenticated|unauthorized|forbidden|session.*expired/.test(messages)
  ) {
    return "Your session has expired. Please refresh the page and try again.";
  }

  if (statusCode === 429 || /rate.?limit|too many requests/.test(messages)) {
    return "Too many payment attempts. Please wait a moment and try again.";
  }

  if (
    /email/.test(messages) &&
    /invalid|valid|required|missing/.test(messages)
  ) {
    return "Please check your email address and try again.";
  }

  if (
    /(amount|currency)/.test(messages) &&
    /invalid|unsupported|required|minimum|maximum|missing/.test(messages)
  ) {
    return "Please check the payment amount and currency, then try again.";
  }

  if (
    /stripe|payment.?intent|connected.?account|destination|capabilit|charges.?enabled|onboard/.test(
      messages
    )
  ) {
    return "Card payments aren't available for this gift right now. Please try another payment option or contact the host.";
  }

  return PAYMENT_FALLBACK;
};
